import { ColDef, ColGroupDef } from 'ag-grid-community';
import {
    ADMIN,
    BUDGET_USER,
    DECISION_USER,
    EMAIL,
    HUMAN_RESOURCES_USER,
    IS_ACTIVE,
    ROLES,
    SCOPES,
    USER_RIGHTS,
} from 'business/constants';
import { ScopeT, UserRole, EnrichedUserT } from 'business/definitions';
import { CellStyleT } from 'ui/AgGridReact/colDefConfig';
import { isAdminBudget } from 'hooks/user';
import { accessTypeComparator } from './gridHelper';
import { FINANCE_SCOPE } from 'routes/BudgetModule/config/constants';

export const ACTIONS_FIELD = 'actions';

const cellStyleCentered: CellStyleT = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0px',
};

const getUserRoleColumn = (userRole: UserRole, t: TranslationT): ColDef => ({
    field: ROLES,
    headerName: t(`adminSpace.usersList.${userRole}`),
    cellRenderer: 'booleanCellRenderer',
    cellStyle: cellStyleCentered,
    maxWidth: 90,
    valueGetter: ({ data }: { data: EnrichedUserT }) =>
        data.roles.includes(userRole),
});

const getAdminBudgetColumn = (t: TranslationT): ColDef => ({
    field: ROLES,
    headerName: t(`adminSpace.usersList.adminbudget`),
    cellRenderer: 'booleanCellRenderer',
    cellStyle: cellStyleCentered,
    maxWidth: 90,
    valueGetter: ({ data }: { data: EnrichedUserT }) =>
        isAdminBudget(data.roles),
});

const getScopeColumn = (scope: ScopeT): ColDef => ({
    field: SCOPES,
    headerName: scope.label,
    cellClass: `e2e-scopes-${scope.name}`,
    cellRenderer: 'accessTypeCellRenderer',
    cellStyle: cellStyleCentered,
    comparator: accessTypeComparator,
    valueGetter: ({ data }: { data: EnrichedUserT }) =>
        data.scopes?.find((s) => s.name === scope.name)?.accessType,
});

const getManageUsersColumns = ({
    hasDecision,
    hasBudget,
    canUserSeeHumanResourcesSettings,
    t,
}: {
    hasDecision: boolean;
    hasBudget: boolean;
    canUserSeeHumanResourcesSettings: boolean;
    t: TranslationT;
}): ColGroupDef => {
    const children: ColDef[] = [
        {
            field: EMAIL,
            headerName: t('common.email'),
            pinned: 'left',
            minWidth: 200,
            resizable: true,
            cellRenderer: 'tooltipCellRenderer',
            cellRendererParams: { colored: false },
        },
        {
            field: IS_ACTIVE,
            headerName: t('common.active'),
            cellRenderer: 'booleanCellRenderer',
            cellStyle: cellStyleCentered,
            maxWidth: 90,
        },
        getUserRoleColumn(ADMIN, t),
    ];
    if (hasDecision) children.push(getUserRoleColumn(DECISION_USER, t));
    if (hasBudget) {
        children.push(getUserRoleColumn(BUDGET_USER, t));
        children.push(getAdminBudgetColumn(t));
    }
    if (canUserSeeHumanResourcesSettings) {
        children.push(getUserRoleColumn(HUMAN_RESOURCES_USER, t));
    }
    return {
        headerName: t('adminSpace.usersList.manageUsers'),
        children,
        headerClass: 'ag-columns-group',
    };
};

export const getColumns = ({
    hasDecision,
    hasBudget,
    canUserSeeHumanResourcesSettings,
    clientScopes,
    users,
    t,
}: {
    hasDecision: boolean;
    hasBudget: boolean;
    canUserSeeHumanResourcesSettings: boolean;
    clientScopes: ScopeT[];
    users: EnrichedUserT[];
    t: TranslationT;
}): (ColDef | ColGroupDef)[] => {
    const columns: (ColDef | ColGroupDef)[] = [
        getManageUsersColumns({
            hasDecision,
            hasBudget,
            canUserSeeHumanResourcesSettings,
            t,
        }),
    ];
    columns.push({
        headerName: t('adminSpace.usersList.scopes'),
        headerClass: 'ag-columns-group',
        children: clientScopes.map(getScopeColumn),
    });
    if (hasBudget)
        columns.push({
            headerName: t('adminSpace.usersList.budgetAccess'),
            headerClass: 'ag-columns-group',
            cellClass: `e2e-budget-access`,
            children: [
                {
                    field: USER_RIGHTS,
                    headerName: t('adminSpace.budgetPreparation'),
                    cellRenderer: 'accessTypeCellRenderer',
                    cellStyle: cellStyleCentered,
                    comparator: accessTypeComparator,
                    valueGetter: ({ data }: { data: EnrichedUserT }) =>
                        data.writeScopes?.find((s) => s.name === FINANCE_SCOPE)
                            ?.accessType,
                },
            ],
        });
    columns.push({
        children: [
            {
                field: ACTIONS_FIELD,
                headerName: t('adminSpace.usersList.actions'),
                cellRenderer: 'actionsCellRenderer',
                cellRendererParams: {
                    existingUsersEmails: users.map(
                        (user: EnrichedUserT) => user.email,
                    ),
                },
                cellStyle: cellStyleCentered,
                sortable: false,
                minWidth: 90,
                valueGetter: ({ data }: { data: EnrichedUserT }) => data,
            },
        ],
    });
    return columns;
};
